import { useEffect, useState } from 'react';
import { useFieldArray, Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FormField, FormItem, FormControl, FormMessage,
} from '@/components/ui/form';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { categoryRequires } from '@/constants/expenseCategories';
import { EmployeeApiClient } from '@/clients/employeeApiClient';
import { useCompany } from '@/hooks/useCompany';

type ParticipantRow = {
  source: 'employee' | 'external';
  employee_id?: string;
  name: string;
  company: string;
};

interface ParticipantsFieldProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  categoryFieldName: string;     // e.g., 'expenseCategory'
  participantsFieldName: string; // e.g., 'participants'
}

export function ParticipantsField({
  control, setValue, watch, categoryFieldName, participantsFieldName,
}: ParticipantsFieldProps) {
  const t = useTranslations('consultantDraft.participants');
  const category = watch(categoryFieldName);
  const { company } = useCompany();
  const currentCompanyName = company?.name ?? '';
  const required = categoryRequires(category, 'participants');

  const { fields, append, remove } = useFieldArray({
    control,
    name: participantsFieldName,
  });

  // Seed one empty row the first time the section becomes required;
  // clear the array when the section becomes non-required.
  useEffect(() => {
    if (required && fields.length === 0) {
      append({ source: 'external', name: '', company: '' } as ParticipantRow);
    }
    if (!required && fields.length > 0) {
      setValue(participantsFieldName, [], { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required]);

  // Fetch employees lazily when the section first appears
  const [employees, setEmployees] = useState<Array<{ id: string; name: string }>>([]);
  const [employeesLoadError, setEmployeesLoadError] = useState(false);
  useEffect(() => {
    if (!required || employees.length > 0) return;
    const client = new EmployeeApiClient();
    client.listEmployees()
      .then(list => setEmployees(
        list.map(e => ({ id: e.id, name: e.display_name ?? e.name ?? '' })),
      ))
      .catch(() => setEmployeesLoadError(true));
  }, [required, employees.length]);

  if (!required) return null;

  return (
    <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
      <div>
        <h3 className="text-sm font-medium">{t('heading')}</h3>
        <p className="text-xs text-muted-foreground mt-1">
          {t('help')}
        </p>
      </div>

      {employeesLoadError && (
        <p className="text-xs text-amber-600">
          {t('loadFailed')}
        </p>
      )}

      <div className="space-y-2">
        {fields.map((field, index) => (
          <ParticipantRowEditor
            key={field.id}
            index={index}
            control={control}
            setValue={setValue}
            watch={watch}
            participantsFieldName={participantsFieldName}
            employees={employees}
            currentCompanyName={currentCompanyName}
            canRemove={fields.length > 1}
            onRemove={() => remove(index)}
          />
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => append({ source: 'external', name: '', company: '' } as ParticipantRow)}
      >
        <Plus className="mr-2 h-4 w-4" /> {t('add')}
      </Button>

      <FormField
        control={control}
        name={participantsFieldName}
        render={() => (
          <FormItem>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function ParticipantRowEditor({
  index, control, setValue, watch, participantsFieldName,
  employees, currentCompanyName, canRemove, onRemove,
}: {
  index: number;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  participantsFieldName: string;
  employees: Array<{ id: string; name: string }>;
  currentCompanyName: string;
  canRemove: boolean;
  onRemove: () => void;
}) {
  const t = useTranslations('consultantDraft.participants');
  const rowPath = `${participantsFieldName}.${index}`;
  const row = watch(rowPath) as ParticipantRow | undefined;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = employees.filter(e =>
    e.name.toLowerCase().includes(query.toLowerCase()),
  );

  const pickEmployee = (emp: { id: string; name: string }) => {
    setValue(`${rowPath}.source`, 'employee', { shouldDirty: true, shouldValidate: true });
    setValue(`${rowPath}.employee_id`, emp.id, { shouldDirty: true, shouldValidate: true });
    setValue(`${rowPath}.name`, emp.name, { shouldDirty: true, shouldValidate: true });
    // Default company = current company; user can override
    if (!row?.company) {
      setValue(`${rowPath}.company`, currentCompanyName, { shouldDirty: true, shouldValidate: true });
    }
    setOpen(false);
  };

  const addExternal = () => {
    setValue(`${rowPath}.source`, 'external', { shouldDirty: true, shouldValidate: true });
    setValue(`${rowPath}.employee_id`, undefined, { shouldDirty: true });
    setValue(`${rowPath}.name`, query, { shouldDirty: true, shouldValidate: true });
    // Leave company empty for externals — user must fill in
    setOpen(false);
  };

  return (
    <div className="flex items-start gap-2">
      <div className="flex-1">
        <FormField
          control={control}
          name={`${rowPath}.name`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      className={cn('w-full justify-between', !field.value && 'text-muted-foreground')}
                    >
                      {field.value || t('pickOrType')}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={t('search')}
                        value={query}
                        onValueChange={setQuery}
                      />
                      <CommandList label={t('employees')}>
                        {filtered.length > 0 && (
                          <CommandGroup heading={t('employees')}>
                            {filtered.map(emp => (
                              <CommandItem
                                key={emp.id}
                                value={emp.name}
                                onSelect={() => pickEmployee(emp)}
                              >
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    row?.employee_id === emp.id ? 'opacity-100' : 'opacity-0',
                                  )}
                                />
                                {emp.name}
                                <span className="ml-2 text-xs text-muted-foreground">{t('internalStaff')}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                        {query.trim() !== '' && (
                          <CommandGroup heading={t('external')}>
                            <CommandItem onSelect={addExternal}>
                              <Plus className="mr-2 h-4 w-4" />
                              {t('addExternal', { name: query })}
                            </CommandItem>
                          </CommandGroup>
                        )}
                        {filtered.length === 0 && query.trim() === '' && (
                          <CommandEmpty>{t('noEmployees')}</CommandEmpty>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="flex-1">
        <FormField
          control={control}
          name={`${rowPath}.company`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={t('company')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="pt-2 text-xs text-muted-foreground whitespace-nowrap">
        {row?.source === 'employee' ? t('internal') : t('externalTag')}
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        disabled={!canRemove}
        aria-label={t('remove')}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
